import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import { useFormik } from "formik";
import * as yup from "yup";
import Labels from "../labels/Labels";

// Helper function to extract error message from backend response
function getErrorMessage(error, defaultMessage) {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data) {
    return typeof error.response.data === "string"
      ? error.response.data
      : defaultMessage;
  }
  if (error?.message) {
    return error.message;
  }
  return defaultMessage;
}

async function fetchFeeTypes() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/fee-types/all`
  );
  return response;
}

export default function AddAdministrativeFee(props) {
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });
  const [fallbackMonth, setFallbackMonth] = useState("");
  const [fallbackYear, setFallbackYear] = useState("");

  const queryClient = useQueryClient();

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 21 }, (_, i) =>
    String(currentYear - 10 + i)
  );

  const { data: feeTypesData, isLoading: isLoadingFeeTypes } = useQuery(
    ["feeTypes"],
    fetchFeeTypes,
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: employersData, isLoading: isLoadingEmployers } = useQuery(
    ["employers"],
    () =>
      HttpService.getAxiosClient().get(
        window.API_URL + `/employer/all?excludeRelations=true`
      ),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const createAdministrativeFeeMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/administrative-fees`,
        values
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["administrativeFees"]);
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "administrativeFee.createSuccess",
            "Administrative fee created successfully"
          ),
        });
        setTimeout(() => props.handleClose(), 2000);
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: getErrorMessage(
            error,
            Labels.getLabel(
              "administrativeFee.createError",
              "Error creating the administrative fee"
            )
          ),
        });
      },
    }
  );

  const validationSchema = yup.object({
    feeTypeId: yup
      .string()
      .required(
        Labels.getLabel(
          "administrativeFee.feeTypeRequired",
          "Fee Type is required"
        )
      ),
    amount: yup
      .number()
      .typeError(
        Labels.getLabel(
          "administrativeFee.amountMustBeNumber",
          "Amount must be a number"
        )
      )
      .positive(
        Labels.getLabel(
          "administrativeFee.amountMustBePositive",
          "Amount must be positive"
        )
      )
      .required(
        Labels.getLabel(
          "administrativeFee.amountRequired",
          "Amount is required"
        )
      ),
    feeDate: yup
      .date()
      .required(
        Labels.getLabel(
          "administrativeFee.feeDateRequired",
          "Fee Date is required"
        )
      ),
    companyId: yup
      .string()
      .required(
        Labels.getLabel(
          "administrativeFee.companyIdRequired",
          "Company ID is required"
        )
      ),
  });

  const handleFallbackDatePartChange = (field) => (e) => {
    const value = e.target.value;
    const nextYear = field === "year" ? value : fallbackYear;
    const nextMonth = field === "month" ? value : fallbackMonth;

    if (field === "year") {
      setFallbackYear(value);
    } else {
      setFallbackMonth(value);
    }

    if (!nextYear || !nextMonth) {
      formik.setFieldValue("feeDate", "");
      return;
    }

    formik.setFieldValue("feeDate", `${nextYear}-${nextMonth}-01`);
  };

  const formik = useFormik({
    initialValues: {
      feeTypeId: "",
      amount: "",
      feeDate: "",
      companyId: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        amount: parseFloat(values.amount),
      };
      createAdministrativeFeeMutation.mutate(payload);
    },
  });

  useEffect(() => {
    if (!formik.values.feeDate) {
      return;
    }

    const [year, month] = formik.values.feeDate.split("-");
    if (year && month) {
      setFallbackYear(year);
      setFallbackMonth(month);
    }
  }, [formik.values.feeDate]);

  const feeTypeOptions = feeTypesData?.data || [];

  return (
    <Box sx={{ p: 2 }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {/* Fee Type Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={feeTypeOptions}
              getOptionLabel={(option) =>
                option?.id && option?.description
                  ? `${option.id} - ${option.description}`
                  : ""
              }
              value={
                feeTypeOptions.find(
                  (ft) => ft.id === formik.values.feeTypeId
                ) || null
              }
              onChange={(event, newValue) => {
                formik.setFieldValue("feeTypeId", newValue?.id || "");
              }}
              loading={isLoadingFeeTypes}
              disabled={isLoadingFeeTypes}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={Labels.getLabel(
                    "administrativeFee.feeType",
                    "Fee Type"
                  )}
                  error={formik.touched.feeTypeId && !!formik.errors.feeTypeId}
                  helperText={
                    formik.touched.feeTypeId && formik.errors.feeTypeId
                  }
                  required
                />
              )}
            />
          </Grid>

          {/* Amount */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={Labels.getLabel("administrativeFee.amount", "Amount")}
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
              name="amount"
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.amount && !!formik.errors.amount}
              helperText={formik.touched.amount && formik.errors.amount}
              required
            />
          </Grid>

          {/* Fee Date - Month/Year Selection */}
          <Grid item xs={12} sm={6}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label={Labels.getLabel(
                    "administrativeFee.feeDateMonth",
                    "Fee Month"
                  )}
                  value={fallbackMonth}
                  onChange={handleFallbackDatePartChange("month")}
                  onBlur={() => formik.setFieldTouched("feeDate", true, true)}
                  error={formik.touched.feeDate && !!formik.errors.feeDate}
                  required
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="01">January</MenuItem>
                  <MenuItem value="02">February</MenuItem>
                  <MenuItem value="03">March</MenuItem>
                  <MenuItem value="04">April</MenuItem>
                  <MenuItem value="05">May</MenuItem>
                  <MenuItem value="06">June</MenuItem>
                  <MenuItem value="07">July</MenuItem>
                  <MenuItem value="08">August</MenuItem>
                  <MenuItem value="09">September</MenuItem>
                  <MenuItem value="10">October</MenuItem>
                  <MenuItem value="11">November</MenuItem>
                  <MenuItem value="12">December</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label={Labels.getLabel(
                    "administrativeFee.feeDateYear",
                    "Fee Year"
                  )}
                  value={fallbackYear}
                  onChange={handleFallbackDatePartChange("year")}
                  onBlur={() => formik.setFieldTouched("feeDate", true, true)}
                  error={formik.touched.feeDate && !!formik.errors.feeDate}
                  required
                >
                  <MenuItem value="">Select</MenuItem>
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                {formik.touched.feeDate && formik.errors.feeDate ? (
                  <Box
                    sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
                  >
                    {formik.errors.feeDate}
                  </Box>
                ) : null}
              </Grid>
            </Grid>
          </Grid>

          {/* Company Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={employersData?.data || []}
              getOptionLabel={(option) =>
                option?.name && option?.idEmployer
                  ? `${option.name} (${option.idEmployer})`
                  : ""
              }
              value={
                employersData?.data?.find(
                  (emp) => emp.idEmployer === formik.values.companyId
                ) || null
              }
              onChange={(event, newValue) => {
                formik.setFieldValue("companyId", newValue?.idEmployer || "");
              }}
              loading={isLoadingEmployers}
              disabled={isLoadingEmployers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={Labels.getLabel(
                    "administrativeFee.company",
                    "Company"
                  )}
                  error={formik.touched.companyId && !!formik.errors.companyId}
                  helperText={
                    formik.touched.companyId && formik.errors.companyId
                  }
                  required
                />
              )}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => props.handleClose()}
                startIcon={<CloseIcon />}
                disabled={createAdministrativeFeeMutation.isLoading}
              >
                {Labels.getLabel("common.cancel", "Cancel")}
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={createAdministrativeFeeMutation.isLoading}
                startIcon={
                  createAdministrativeFeeMutation.isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {Labels.getLabel("common.save", "Save")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
